import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  Form,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
// Icons are only used by the commented-out Add/Delete/Rotate Panel buttons
// below. Re-enable the import alongside the buttons when that workflow
// is restored.
// import { ArrowClockwise, Files, Dash } from 'react-bootstrap-icons';
import Slider from '@material-ui/core/Slider';

import { segmentPropType, panelsPropType } from '../props';

const toFiniteNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const Revise = ({
  currentPanel,
  selectedRoof,
  selectedPanels,
  panels,
  segments,
  panelAzimuthHandler,
  // Kept in the prop signature for now — the Add/Delete/Rotate ButtonGroup
  // is hidden (commented out) but the parent still passes these handlers,
  // and dropping them from destructuring would force a co-ordinated change
  // in Menu/index.js + index.js for no functional gain.
  // eslint-disable-next-line no-unused-vars
  newPanelHandler,
  // eslint-disable-next-line no-unused-vars
  rotatePanelHandler,
  // eslint-disable-next-line no-unused-vars
  deletePanelHandler,
  clearPanels,
  placementHandler,
  panelPlacementFillMode,
  setPanelPlacementFillMode,
  panelPlacementLayoutMode,
  setPanelPlacementLayoutMode,
}) => {
  const segment = segments[selectedRoof];
  const panel = segment ? panels.find(({ id }) => id === segment.id) : null;
  const panelName =
    currentPanel?.name || currentPanel?.model || 'Not selected';
  const panelPower = currentPanel?.power ? ` (${currentPanel.power}W)` : '';
  const panelAzimuth = panel ? toFiniteNumber(panel.azimuth) : null;
  const segmentAzimuth = segment ? toFiniteNumber(segment.azimuth) : null;
  const canEditAzimuth =
    panelAzimuth !== null && segmentAzimuth !== null;

  // We intentionally allow rendering the placement controls when a segment
  // is selected even if there is no `panel` entry yet — that's the very
  // case where the user wants to fill panels via this accordion. Only the
  // azimuth controls actually require existing panel data.

  return (
    <>
      <p className="mb-2">
        <strong>Current Panel:</strong> {panelName}
        {panelPower}
      </p>

      {!segment && (
        <p className="mb-2">
          Click on a roof segment to fill panels or adjust their azimuth.
        </p>
      )}

      {segment && panel && canEditAzimuth && (
        <>
          <h6 className="font-weight-bold">Azimuth</h6>
          <Row>
            <Col sm={6}>
              <Form.Control
                value={Math.round(panelAzimuth * 2) / 2}
                readOnly
              />
            </Col>
          </Row>
          <Slider
            value={panelAzimuth}
            min={segmentAzimuth - 7.5}
            max={segmentAzimuth + 7.5}
            step={0.5}
            aria-labelledby="Azimuth"
            onChange={(e, azimuth) => {
              const nextAzimuth = Number(azimuth);
              if (!Number.isFinite(nextAzimuth)) return;
              panelAzimuthHandler(selectedRoof, +nextAzimuth.toFixed(2));
            }}
          />
        </>
      )}

      {/* Add / Delete / Rotate Panel — hidden per request, do not delete.
          Restore by uncommenting this block AND the icon import above. */}
      {/*
      <ButtonGroup className="w-100 btn-group-sm">
        <Button variant="outline-primary" onClick={newPanelHandler}>
          <Files />
          Panel
        </Button>
        <Button
          variant="outline-primary"
          onClick={deletePanelHandler}
          disabled={!selectedPanels}
        >
          <Dash />
          Panel
        </Button>
        <Button
          variant="outline-primary"
          onClick={rotatePanelHandler}
          disabled={!selectedPanels}
        >
          <ArrowClockwise />
          Panel
        </Button>
      </ButtonGroup>
      */}

      {/* Standalone "Clear Panels on Selected Roof" — superseded by the
          Clear button inside the Fill+Clear group below (since
          panelPlacementOnlySelectedRoof is now derived from selection,
          that Clear scopes to the selected roof automatically). Kept
          commented in case a dedicated label is preferred later. */}
      {/*
      {clearPanels && segment && (
        <Row className="mt-2">
          <Col>
            <Button block onClick={() => clearPanels()}>
              Clear Panels on Selected Roof
            </Button>
          </Col>
        </Row>
      )}
      */}

      {segment && (
        <>
          <Row className="mt-2 mr-3">
            <Col>
              <Form.Control
                as="select"
                value={panelPlacementFillMode}
                onChange={(e) =>
                  setPanelPlacementFillMode(Number(e.target.value))
                }
              >
                <option value={3}>Vertical First</option>
                <option value={4}>Horizontal First</option>
                <option value={2}>Vertical</option>
                <option value={1}>Horizontal</option>
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-1 mr-3">
            <Col>
              <ToggleButtonGroup type="checkbox" style={{ width: '100%' }}>
                <ToggleButton
                  value={1}
                  style={{
                    color: panelPlacementLayoutMode === 1 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(1)}
                >
                  Auto
                </ToggleButton>
                <ToggleButton
                  value={2}
                  style={{
                    color: panelPlacementLayoutMode === 2 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(2)}
                >
                  Grid-Mode
                </ToggleButton>
                <ToggleButton
                  value={3}
                  style={{
                    color: panelPlacementLayoutMode === 3 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(3)}
                >
                  Brick-Mode
                </ToggleButton>
                <ToggleButton
                  value={4}
                  style={{
                    color: panelPlacementLayoutMode === 4 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(4)}
                >
                  Compact
                </ToggleButton>
              </ToggleButtonGroup>
            </Col>
          </Row>
          <Row className="mt-1 mr-3">
            <Col>
              <ButtonGroup style={{ width: '100%' }}>
                <Button
                  onClick={(e) =>
                    placementHandler(
                      e,
                      false,
                      panelPlacementFillMode,
                      panelPlacementLayoutMode
                    )
                  }
                  disabled={!currentPanel}
                >
                  Fill Panels
                </Button>
                <Button
                  onClick={() => clearPanels && clearPanels()}
                  disabled={!currentPanel}
                >
                  Clear
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

Revise.propTypes = {
  currentPanel: PropTypes.object,
  selectedRoof: PropTypes.number.isRequired,
  selectedPanels: PropTypes.bool.isRequired,
  panels: panelsPropType.isRequired,
  segments: segmentPropType.isRequired,
  panelAzimuthHandler: PropTypes.func.isRequired,
  newPanelHandler: PropTypes.func.isRequired,
  rotatePanelHandler: PropTypes.func.isRequired,
  deletePanelHandler: PropTypes.func.isRequired,
  clearPanels: PropTypes.func,
  placementHandler: PropTypes.func.isRequired,
  panelPlacementFillMode: PropTypes.number.isRequired,
  setPanelPlacementFillMode: PropTypes.func.isRequired,
  panelPlacementLayoutMode: PropTypes.number.isRequired,
  setPanelPlacementLayoutMode: PropTypes.func.isRequired,
};

Revise.defaultProps = {
  currentPanel: null,
  clearPanels: null,
};

export default Revise;
